import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import ShipmentDetailDrawer from '../components/ShipmentDetailDrawer';
import CreateShipmentModal from '../components/CreateShipmentModal';

// ─── Types ──────────────────────────────────────────────────────────
interface ShipmentRow {
  id: string;
  order_number: string;
  sidemark: string | null;
  dealer_name: string;
  shipping_address: string | Record<string, any> | null;
  selected_carrier_name: string | null;
  freight_total_amount: number | string | null;
  freight_base_amount: number | string | null;
  freight_booked_at: string | null;
  freight_canceled_at: string | null;
  freight_tracking_status: string | null;
  freightview_shipment_id: string | null;
  freight_bol_number: string | null;
  freight_pro_number: string | null;
  freight_delivery_estimate: string | null;
  freight_documents: any;
  freight_tracking_events: any;
  freight_shipment_details_url: string | null;
  created_at: string;
  status: string;
}

interface ParsedAddress {
  city?: string;
  state?: string;
  zip?: string;
  street?: string;
  company?: string;
}

type TabStatus = 'all' | 'unbooked' | 'booked' | 'in_transit' | 'delivered' | 'canceled';

const TABS: { key: TabStatus; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'unbooked', label: 'Unbooked' },
  { key: 'booked', label: 'Booked' },
  { key: 'in_transit', label: 'In Transit' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'canceled', label: 'Canceled' },
];

// ─── Helpers ────────────────────────────────────────────────────────
function parseAddress(raw: string | Record<string, any> | null | undefined): ParsedAddress {
  if (!raw) return {};
  let parsed: any = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  // Normalize: ship_to_address stores "street", map to our interface
  if (parsed.street && !parsed.company) {
    return { ...parsed, street: parsed.street };
  }
  return parsed as ParsedAddress;
}

function resolveTrackingStatus(row: ShipmentRow): string {
  if (row.freight_canceled_at) return 'canceled';
  if (row.freight_tracking_status) return row.freight_tracking_status.toLowerCase();
  if (row.freight_booked_at) return 'booked';
  return 'unbooked';
}

function formatCurrency(value: number | string | null | undefined): string {
  const num = Number(value);
  if (isNaN(num) || value === null || value === undefined) return '$0.00';
  return '$' + num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Status Badge ───────────────────────────────────────────────────
function renderStatusBadge(trackingStatus: string): JSX.Element {
  const config: Record<string, { bg: string; text: string; label: string }> = {
    unbooked:     { bg: 'bg-yellow-900',  text: 'text-yellow-300', label: 'Unbooked' },
    confirmed:    { bg: 'bg-blue-900',    text: 'text-blue-300',   label: 'Booked' },
    booked:       { bg: 'bg-blue-900',    text: 'text-blue-300',   label: 'Booked' },
    'picked-up':  { bg: 'bg-purple-900',  text: 'text-purple-300', label: 'Picked Up' },
    in_transit:   { bg: 'bg-indigo-900',  text: 'text-indigo-300', label: 'In Transit' },
    delivered:    { bg: 'bg-green-900',   text: 'text-green-300',  label: 'Delivered' },
    canceled:     { bg: 'bg-red-900',     text: 'text-red-300',    label: 'Canceled' },
  };
  const c = config[trackingStatus] || config.unbooked;
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
}

// ─── Component ──────────────────────────────────────────────────────
export default function ShipmentsPage() {
  const [shipments, setShipments] = useState<ShipmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabStatus>('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [drawerRow, setDrawerRow] = useState<ShipmentRow | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [syncingId, setSyncingId] = useState<string | null>(null);

  // Debounce search input
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 350);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [search]);

  // ─── Data Fetching ──────────────────────────────────────────────
  const loadShipments = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (activeTab !== 'all') params.status = activeTab;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;

      const data = await api.searchFVShipments(params);
      setShipments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load shipments:', error);
      setShipments([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, debouncedSearch, dateFrom, dateTo]);

  useEffect(() => {
    loadShipments();
  }, [loadShipments]);

  // ─── Actions ────────────────────────────────────────────────────
  const handleSync = async (e: React.MouseEvent, orderId: string) => {
    e.stopPropagation();
    setSyncingId(orderId);
    try {
      await api.syncOrderTracking(orderId);
      await loadShipments();
    } catch (error) {
      console.error('Sync failed:', error);
      alert(error instanceof Error ? error.message : 'Failed to sync tracking');
    } finally {
      setSyncingId(null);
    }
  };

  const handleViewDetail = (e: React.MouseEvent, row: ShipmentRow) => {
    e.stopPropagation();
    setDrawerRow(row);
  };

  const handleRowClick = (row: ShipmentRow) => {
    setDrawerRow(row);
  };

  const handleCreateComplete = () => {
    setShowCreateModal(false);
    loadShipments();
  };

  // ─── Counts per tab (local filter on loaded data for badge counts) ──
  const countByTab = (tab: TabStatus): number | null => {
    if (tab === 'all') return null;
    // Only show counts when on the 'all' tab (or always show them)
    return shipments.filter((s) => {
      const ts = resolveTrackingStatus(s);
      if (tab === 'booked') return ts === 'booked' || ts === 'confirmed';
      return ts === tab;
    }).length;
  };

  // ─── Render ─────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Shipments</h1>
          <p className="text-gray-400 mt-1">Manage freight shipments and tracking</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 text-sm font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Shipment
          </button>
          <button
            onClick={loadShipments}
            disabled={loading}
            className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 transition-colors flex items-center gap-2 text-sm"
          >
            <svg
              className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-700">
        <nav className="flex gap-1 -mb-px overflow-x-auto" aria-label="Shipment status tabs">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            const count = activeTab === 'all' ? countByTab(tab.key) : null;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? 'border-indigo-500 text-indigo-400'
                    : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'
                }`}
              >
                {tab.label}
                {count !== null && count > 0 && (
                  <span className="ml-2 px-1.5 py-0.5 text-xs rounded-full bg-gray-700 text-gray-300">
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search order number or dealer name..."
            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <label className="absolute -top-2 left-2 px-1 bg-gray-900 text-[10px] text-gray-500 uppercase tracking-wider">
              From
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
            />
          </div>
          <div className="relative">
            <label className="absolute -top-2 left-2 px-1 bg-gray-900 text-[10px] text-gray-500 uppercase tracking-wider">
              To
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
            />
          </div>
          {(dateFrom || dateTo) && (
            <button
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              className="px-3 py-2 text-xs text-gray-400 hover:text-white border border-gray-700 rounded-lg hover:bg-gray-800 transition-colors"
              title="Clear date filters"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      ) : shipments.length === 0 ? (
        <div className="bg-gray-800 rounded-xl p-12 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
            />
          </svg>
          <h3 className="text-lg font-medium text-white mb-2">No Shipments Found</h3>
          <p className="text-gray-400">
            {debouncedSearch || dateFrom || dateTo || activeTab !== 'all'
              ? 'Try adjusting your filters or search query.'
              : 'There are no freight shipments to display.'}
          </p>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Order #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Dealer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Destination
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Carrier
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Cost
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {shipments.map((row) => {
                  const addr = parseAddress(row.shipping_address);
                  const trackingStatus = resolveTrackingStatus(row);
                  const streetLine = addr.street || '';
                  const cityLine = [addr.city, addr.state].filter(Boolean).join(', ') + (addr.zip ? ' ' + addr.zip : '');

                  return (
                    <tr
                      key={row.id}
                      onClick={() => handleRowClick(row)}
                      className="hover:bg-gray-750 cursor-pointer transition-colors"
                    >
                      {/* Order # */}
                      <td className="px-4 py-4">
                        <div className="text-white font-medium">{row.order_number}</div>
                        {row.sidemark && <div className="text-gray-400 text-xs font-medium">{row.sidemark}</div>}
                        <div className="text-gray-500 text-xs">{formatDate(row.created_at)}</div>
                      </td>

                      {/* Dealer */}
                      <td className="px-4 py-4">
                        <div className="text-gray-300 text-sm">{row.dealer_name || '-'}</div>
                      </td>

                      {/* Destination */}
                      <td className="px-4 py-4">
                        {streetLine && <div className="text-gray-300 text-sm">{streetLine}</div>}
                        <div className={`text-sm ${streetLine ? 'text-gray-500' : 'text-gray-300'}`}>{cityLine || '-'}</div>
                      </td>

                      {/* Carrier */}
                      <td className="px-4 py-4">
                        <div className="text-gray-300 text-sm">
                          {row.selected_carrier_name || (
                            <span className="text-gray-500 italic">Not assigned</span>
                          )}
                        </div>
                        {row.freight_bol_number && (
                          <div className="text-gray-500 text-xs mt-0.5">
                            BOL: {row.freight_bol_number}
                          </div>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-4">
                        {renderStatusBadge(trackingStatus)}
                        {row.freight_delivery_estimate && trackingStatus === 'in_transit' && (
                          <div className="text-gray-500 text-xs mt-1">
                            ETA: {formatDate(row.freight_delivery_estimate)}
                          </div>
                        )}
                      </td>

                      {/* Cost */}
                      <td className="px-4 py-4 text-right">
                        <div className="text-green-400 font-medium text-sm">
                          {formatCurrency(row.freight_total_amount)}
                        </div>
                        {row.freight_base_amount &&
                          Number(row.freight_base_amount) !== Number(row.freight_total_amount) && (
                            <div className="text-gray-500 text-xs mt-0.5">
                              Base: {formatCurrency(row.freight_base_amount)}
                            </div>
                          )}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-4 text-right">
                        <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={(e) => handleViewDetail(e, row)}
                            className="px-3 py-1.5 text-xs font-medium bg-gray-700 text-gray-200 rounded-lg hover:bg-gray-600 transition-colors"
                          >
                            View
                          </button>
                          <button
                            onClick={(e) => handleSync(e, row.id)}
                            disabled={syncingId === row.id}
                            className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
                            title="Sync tracking status"
                          >
                            <svg
                              className={`w-4 h-4 ${syncingId === row.id ? 'animate-spin' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                              />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Result count footer */}
          <div className="px-4 py-3 bg-gray-900 border-t border-gray-700">
            <p className="text-xs text-gray-500">
              Showing {shipments.length} shipment{shipments.length !== 1 ? 's' : ''}
              {activeTab !== 'all' && (
                <span>
                  {' '}with status <span className="text-gray-400 font-medium">{activeTab.replace('_', ' ')}</span>
                </span>
              )}
              {debouncedSearch && (
                <span>
                  {' '}matching "<span className="text-gray-400">{debouncedSearch}</span>"
                </span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Shipment Detail Drawer */}
      {drawerRow && (
        <ShipmentDetailDrawer
          order={drawerRow}
          onClose={() => setDrawerRow(null)}
          onRefresh={async () => {
            await loadShipments();
            // Re-fetch the specific row to update the drawer
            const refreshed = shipments.find((s: any) => s.id === drawerRow.id);
            if (refreshed) setDrawerRow(refreshed);
          }}
        />
      )}

      {/* Create Shipment Modal */}
      {showCreateModal && (
        <CreateShipmentModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCreateComplete}
        />
      )}
    </div>
  );
}
